// ═══════════════════════════════════════════════════════════════
// SignaturePad — Mobile-friendly canvas signature capture
// Touch-safe (touchAction: none), scales to container width.
// Returns base64 PNG via onCapture callback.
// ═══════════════════════════════════════════════════════════════

import { useRef, useState, useCallback, useEffect } from 'react';

interface Props {
  label: string;
  onCapture: (dataUrl: string) => void;
  /** Pre-populate with existing signature (base64 PNG) */
  initial?: string;
  /** Canvas logical height in px (visual height is always 110px via CSS) */
  height?: number;
}

export function SignaturePad({ label, onCapture, initial, height = 180 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [signed, setSigned] = useState(false);

  // Restore initial signature on mount
  useEffect(() => {
    if (!initial || !canvasRef.current) return;
    const img = new Image();
    img.onload = () => {
      const ctx = canvasRef.current?.getContext('2d');
      if (ctx && canvasRef.current) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0, canvasRef.current.width, canvasRef.current.height);
        setSigned(true);
      }
    };
    img.src = initial;
  }, [initial]);

  const getPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.nativeEvent as MouseEvent).offsetX * scaleX,
      y: (e.nativeEvent as MouseEvent).offsetY * scaleY,
    };
  }, []);

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(e);
    setDrawing(true);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, [getPos]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#1e293b';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [drawing, getPos]);

  const endDraw = useCallback(() => {
    if (!drawing) return;
    setDrawing(false);
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      onCapture(dataUrl);
      setSigned(true);
    }
  }, [drawing, onCapture]);

  const clear = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx && canvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setSigned(false);
      onCapture('');
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{label}</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {signed && (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#059669' }}>✓ Signed</span>
          )}
          <button
            type="button"
            onClick={clear}
            style={{ fontSize: 12, color: '#939598', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            Clear
          </button>
        </div>
      </div>
      <canvas
        ref={canvasRef}
        width={600}
        height={height}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
        style={{
          width: '100%',
          height: 110,
          border: `2px solid ${signed ? '#059669' : '#e2e8f0'}`,
          borderRadius: 4,
          background: signed ? '#f0fdf4' : '#fafafa',
          touchAction: 'none',
          cursor: 'crosshair',
          display: 'block',
        }}
      />
      <div style={{ fontSize: 12, color: '#939598', marginTop: 4, textAlign: 'center' }}>
        {signed ? 'Tap "Clear" to redo signature' : 'Sign with your finger or stylus'}
      </div>
    </div>
  );
}
